'use client';

import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';


interface Project {
  id: string;
  title: string;
  client: string;
  industry: string;
  year: string;
  description: string;
  technologies: string[];
  image: string;
  imageAlt: string;
  featured: boolean;
}

export default function ProjectsGrid() {
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const projects: Project[] = [
  {
    id: 'project_1',
    title: 'E-Commerce Platform Redesign',
    client: 'Fashion Retailer UK',
    industry: 'Retail',
    year: '2025',
    description:
    'Complete redesign and migration of legacy e-commerce platform to modern stack. Implemented responsive design with mobile-first approach, integrated payment gateway, and built custom inventory management system. Result: 45% increase in conversion rates, 60% improvement in page load speed, and 30% reduction in cart abandonment.',
    technologies: ['React', 'Next.js', 'Stripe', 'AWS', 'PostgreSQL', 'Redis'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b208f12-1768225455029.png",
    imageAlt: 'Modern e-commerce website dashboard showing analytics and product management interface',
    featured: true
  },
  {
    id: 'project_2',
    title: 'Cloud Infrastructure Migration',
    client: 'Financial Services',
    industry: 'Finance',
    year: '2025',
    description:
    'Migrated on-premise infrastructure to AWS cloud with zero downtime. Designed microservices architecture, implemented CI/CD pipelines, and established automated backup systems. Outcome: 35% reduction in operational costs, 99.99% uptime achieved, and scalability improved by 300%.',
    technologies: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'Jenkins', 'Prometheus'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_183cca85d-1767580178726.png",
    imageAlt: 'Cloud computing infrastructure diagram with network connections and server architecture',
    featured: true
  },
  {
    id: 'project_3',
    title: 'Cybersecurity Audit & Implementation',
    client: 'Healthcare Provider',
    industry: 'Healthcare',
    year: '2024',
    description:
    'Comprehensive security audit and implementation of enterprise-grade security solutions. Deployed multi-factor authentication, encrypted data storage, intrusion detection systems, and conducted staff training. Achieved full GDPR compliance and ISO 27001 certification readiness.',
    technologies: ['Security', 'Compliance', 'Monitoring', 'Encryption', 'SIEM'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11445e064-1764675714504.png",
    imageAlt: 'Cybersecurity professional analyzing security metrics on multiple monitoring screens',
    featured: false
  },
  {
    id: 'project_4',
    title: 'Custom CRM Development',
    client: 'Real Estate Agency',
    industry: 'Real Estate',
    year: '2024',
    description:
    'Built bespoke CRM system with property management, client tracking, automated workflows, and email marketing integration. Features include document management, appointment scheduling, and advanced reporting. Result: 50% reduction in administrative time and 40% increase in client retention.',
    technologies: ['Node.js', 'React', 'PostgreSQL', 'Redis', 'TypeScript', 'GraphQL'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_146254e45-1764672099813.png",
    imageAlt: 'Custom CRM dashboard interface showing customer data and analytics visualizations',
    featured: false
  },
  {
    id: 'project_5',
    title: 'Mobile App Development',
    client: 'Logistics Company',
    industry: 'Logistics',
    year: '2024',
    description:
    'Cross-platform mobile app for real-time package tracking and delivery management. Integrated GPS tracking, push notifications, digital signatures, and route optimization. Deployed to 500+ drivers with offline functionality. Achieved 4.8-star rating on app stores.',
    technologies: ['React Native', 'Firebase', 'Google Maps API', 'Redux', 'WebSocket'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1074c4911-1764672106381.png",
    imageAlt: 'Mobile application interface showing logistics tracking and delivery management features',
    featured: false
  },
  {
    id: 'project_6',
    title: 'IT Infrastructure Modernization',
    client: 'Manufacturing SME',
    industry: 'Manufacturing',
    year: '2023',
    description:
    'Complete overhaul of IT infrastructure including network upgrade, server consolidation, virtualization, and 24/7 monitoring implementation. Established disaster recovery plan and automated backup systems. Result: 70% reduction in downtime and 25% cost savings on hardware.',
    technologies: ['Networking', 'Monitoring', 'Support', 'VMware', 'Backup Solutions'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1828003bb-1766492797726.png",
    imageAlt: 'Modern IT server room with organized network equipment and cable management',
    featured: false
  },
  {
    id: 'project_7',
    title: 'Corporate Website & Branding',
    client: 'Legal Firm',
    industry: 'Legal Services',
    year: '2024',
    description:
    'Complete web design and branding project including logo design, brand guidelines, and responsive website development. Implemented content management system, client portal, and SEO optimization. Outcome: 200% increase in organic traffic and 85% improvement in lead generation.',
    technologies: ['Next.js', 'Tailwind CSS', 'Headless CMS', 'SEO', 'Analytics'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6ec7e4a-1764660533966.png",
    imageAlt: 'Professional corporate website design with modern branding and clean layout',
    featured: false
  },
  {
    id: 'project_8',
    title: 'Enterprise Software Development',
    client: 'Education Institution',
    industry: 'Education',
    year: '2023',
    description:
    'Custom learning management system with student portal, course management, grading system, and video conferencing integration. Built with scalability for 10,000+ concurrent users. Features include automated attendance tracking, assignment submission, and parent communication portal.',
    technologies: ['Python', 'Django', 'React', 'PostgreSQL', 'WebRTC', 'AWS'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_160ee9605-1766866233459.png",
    imageAlt: 'Educational software platform showing course management and student dashboard interface',
    featured: false
  },
  {
    id: 'project_9',
    title: 'API Development & Integration',
    client: 'Travel Agency',
    industry: 'Travel & Tourism',
    year: '2024',
    description:
    'Developed RESTful API and integrated multiple third-party booking systems including flights, hotels, and car rentals. Implemented real-time pricing engine, payment processing, and automated booking confirmations. Result: 90% reduction in manual booking time and seamless multi-platform integration.',
    technologies: ['Node.js', 'Express', 'MongoDB', 'REST API', 'OAuth', 'Payment Gateways'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0f813b4-1764664292833.png",
    imageAlt: 'API integration dashboard showing connected services and data flow visualization',
    featured: false
  }];


  const filters = [
  { id: 'filter_all', label: 'All Projects', value: 'all' },
  { id: 'filter_web', label: 'Web Development', value: 'web' },
  { id: 'filter_cloud', label: 'Cloud', value: 'cloud' },
  { id: 'filter_security', label: 'Security', value: 'security' }];


  const filteredProjects = projects.filter((project) => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'web')
    return project.technologies.some((tech) =>
    ['React', 'Next.js', 'Node.js'].includes(tech)
    );
    if (selectedFilter === 'cloud')
    return project.technologies.some((tech) =>
    ['AWS', 'Docker', 'Kubernetes'].includes(tech)
    );
    if (selectedFilter === 'security')
    return project.technologies.some((tech) =>
    ['Security', 'Compliance'].includes(tech)
    );
    return true;
  });

  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-950">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-16 animate-on-scroll animate-in">
          {filters.map((filter) =>
          <button
            key={filter.id}
            onClick={() => setSelectedFilter(filter.value)}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedFilter === filter.value ?
            'bg-blue-500 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'}`
            }>

              {filter.label}
            </button>
          )}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) =>
          <div
            key={project.id}
            className={`group bg-gray-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-blue-500 transition-all duration-300 animate-on-scroll animate-in-delay-${
            index % 3 + 1} ${
            project.featured ? 'lg:col-span-2' : ''}`}>

              <div
              className={`image-zoom ${
              project.featured ? 'aspect-[16/9]' : 'aspect-[4/3]'} bg-gray-800`
              }>

                <AppImage
                src={project.image}
                alt={project.imageAlt}
                className="w-full h-full object-cover" />

              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  <span>{project.industry}</span>
                  <span>•</span>
                  <span>{project.year}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{project.client}</p>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, idx) =>
                <span
                  key={`${project.id}_tech_${idx}`}
                  className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full">

                      {tech}
                    </span>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}